"use client";

import {
  IconCaretLeft,
  IconCaretRight,
  IconCheckCircleFill,
  IconCircle,
  IconInfo,
  IconXCircleFill,
} from "@decibeltrade/react-icons";
import type { AccountStreaks } from "@decibeltrade/sdk";
import { useTranslations } from "next-intl";
import { useMemo, useState } from "react";
import { css, cx } from "styled-system/css";
import { flex, stack } from "styled-system/patterns";

import { Card } from "@/components/card/card";
import { Tooltip } from "@/components/tooltip";

import { transformStreaksData } from "../utils/transform-streaks";

interface StreakDay {
  date: number;
  traded: boolean;
  isCurrentMonth: boolean;
  isUpcoming?: boolean;
}

interface StreaksCardProps {
  streaksData: AccountStreaks | undefined;
}

const WEEK_DAYS = ["M", "T", "W", "T", "F", "S", "S"] as const;

export function StreaksCard({ streaksData }: StreaksCardProps) {
  const t = useTranslations("points.streaks");

  const now = useMemo(() => new Date(), []);
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  const [displayYear, setDisplayYear] = useState(currentYear);
  const [displayMonth, setDisplayMonth] = useState(currentMonth);

  const isCurrentMonth = displayYear === currentYear && displayMonth === currentMonth;

  const goToPrevMonth = () => {
    if (displayMonth === 0) {
      setDisplayMonth(11);
      setDisplayYear(displayYear - 1);
    } else {
      setDisplayMonth(displayMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (isCurrentMonth) return;
    if (displayMonth === 11) {
      setDisplayMonth(0);
      setDisplayYear(displayYear + 1);
    } else {
      setDisplayMonth(displayMonth + 1);
    }
  };

  const {
    currentStreak,
    daysTraded,
    streakPoints: streakAmps,
    month,
    year,
    calendarDays,
    weeklyStatus,
  } = useMemo(
    () => transformStreaksData(streaksData, displayYear, displayMonth),
    [streaksData, displayYear, displayMonth],
  );

  const getWeekTradedCount = (week: StreakDay[]) => {
    return week.filter((day) => day.traded && day.isCurrentMonth).length;
  };

  return (
    <Card
      background="gradient"
      className={stack({
        p: "0",
        w: "full",
        maxW: { base: "full", lg: "[494px]" },
        rounded: "350",
        overflow: "hidden",
      })}
    >
      {/* Header */}
      <div
        className={flex({
          justify: "space-between",
          alignItems: "center",
          w: "full",
          px: { base: "16", lg: "24" },
          pt: { base: "16", lg: "20" },
          pb: { base: "12", lg: "24" },
        })}
      >
        <h2
          className={css({
            textStyle: { base: "heading.xs", lg: "heading.lg" },
            color: "text.primary",
          })}
        >
          {t("title")}
        </h2>
        <div className={flex({ alignItems: "center", gap: "8" })}>
          <button
            onClick={goToPrevMonth}
            className={css({
              cursor: "pointer",
              color: "text.secondary",
              _hover: { color: "text.primary" },
              display: "flex",
              alignItems: "center",
              p: "4",
            })}
          >
            <IconCaretLeft className={css({ w: "16", h: "16" })} />
          </button>
          <span
            className={css({
              textStyle: { base: "subheading.3xs", lg: "subheading.xs" },
              fontWeight: "medium",
              color: "text.primary",
              minW: { base: "[90px]", lg: "[120px]" },
              textAlign: "center",
            })}
          >
            {month} {year}
          </span>
          <button
            onClick={goToNextMonth}
            disabled={isCurrentMonth}
            className={css({
              cursor: isCurrentMonth ? "default" : "pointer",
              color: isCurrentMonth ? "text.tertiary" : "text.secondary",
              _hover: isCurrentMonth ? {} : { color: "text.primary" },
              display: "flex",
              alignItems: "center",
              p: "4",
            })}
          >
            <IconCaretRight className={css({ w: "16", h: "16" })} />
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div
        className={flex({
          gap: { base: "16", sm: "32", lg: "40" },
          alignItems: "flex-start",
          px: { base: "16", lg: "24" },
          pb: { base: "12", lg: "28" },
          flexWrap: "wrap",
        })}
      >
        <div className={stack({ gap: "2" })}>
          <div className={flex({ gap: "4", alignItems: "center" })}>
            <IconCheckCircleFill className={css({ color: "brandYellow.550", w: "16", h: "16" })} />
            <span
              className={css({
                textStyle: "subheading.3xs",
                color: "text.tertiary",
              })}
            >
              {t("yourStreak")}
            </span>
          </div>
          <span
            className={css({
              textStyle: { base: "subheading.3xs", lg: "subheading.xs" },
              fontWeight: "medium",
              color: "text.primary",
            })}
          >
            {currentStreak}
          </span>
        </div>

        <div className={stack({ gap: "2" })}>
          <div className={flex({ gap: "4", alignItems: "center" })}>
            <IconCheckCircleFill className={css({ color: "brandYellow.550", w: "16", h: "16" })} />
            <span
              className={css({
                textStyle: "subheading.3xs",
                color: "text.tertiary",
              })}
            >
              {t("daysTraded")}
            </span>
          </div>
          <span
            className={css({
              textStyle: { base: "subheading.3xs", lg: "subheading.xs" },
              fontWeight: "medium",
              color: "text.primary",
            })}
          >
            {daysTraded}
          </span>
        </div>

        <div className={stack({ gap: "2" })}>
          <div className={flex({ gap: "4", alignItems: "center" })}>
            <span
              className={css({
                textStyle: "subheading.3xs",
                color: "text.tertiary",
              })}
            >
              {t("streakAmps")}
            </span>
            <Tooltip content={t("streakAmpsTooltip")} placement="top">
              <span className={css({ display: "flex", cursor: "help" })}>
                <IconInfo
                  className={css({
                    w: "14",
                    h: "14",
                    color: "text.tertiary",
                  })}
                />
              </span>
            </Tooltip>
          </div>
          <span
            className={css({
              textStyle: { base: "subheading.3xs", lg: "subheading.xs" },
              fontWeight: "medium",
              color: "text.primary",
            })}
          >
            {streakAmps}
          </span>
        </div>
      </div>

      {/* Calendar */}
      <div
        className={stack({ gap: "0", px: { base: "16", lg: "28" }, pb: { base: "16", lg: "24" } })}
      >
        {/* Week day headers */}
        <div className={flex({ alignItems: "center", w: "full", mb: "8" })}>
          <div
            className={css({
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: { base: "0", lg: "[30px]" },
              flex: "1",
            })}
          >
            {WEEK_DAYS.map((day, i) => (
              <span
                key={i}
                className={css({
                  textStyle: "subheading.3xs",
                  color: "text.secondary",
                  textAlign: "center",
                })}
              >
                {day}
              </span>
            ))}
          </div>
          <div className={css({ w: "32", ml: { base: "8", lg: "16" } })} />
        </div>

        {/* Calendar weeks */}
        <div className={stack({ gap: "12" })}>
          {calendarDays.map((week, weekIndex) => {
            const hasTradedDays = getWeekTradedCount(week) > 0;

            return (
              <div key={weekIndex} className={flex({ alignItems: "center", w: "full" })}>
                <div
                  className={css({
                    display: "grid",
                    gridTemplateColumns: "repeat(7, 1fr)",
                    gap: { base: "0", lg: "[30px]" },
                    position: "relative",
                    flex: "1",
                    py: "4",
                  })}
                >
                  {/* Week background gradient */}
                  {hasTradedDays && (
                    <>
                      <div
                        className={css({
                          position: "absolute",
                          left: "[-3px]",
                          right: "[-3px]",
                          top: "0",
                          bottom: "0",
                          rounded: "full",
                          bg: "[linear-gradient(204.363deg, rgb(51, 38, 2) 0.26841%, rgb(1, 1, 1) 142.85%)]",
                          zIndex: "[0]",
                        })}
                      />
                      <div
                        className={css({
                          position: "absolute",
                          left: "0",
                          right: "0",
                          top: "4",
                          bottom: "4",
                          rounded: "full",
                          bg: "[linear-gradient(154.701deg, rgb(51, 38, 2) 2.6369%, rgb(1, 1, 1) 33.367%)]",
                          zIndex: "[0]",
                        })}
                      />
                    </>
                  )}

                  {/* Day cells */}
                  {week.map((day, dayIndex) => (
                    <div
                      key={dayIndex}
                      className={cx(
                        css({
                          w: { base: "full", lg: "[30px]" },
                          h: { base: "[28px]", lg: "[30px]" },
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          position: "relative",
                          zIndex: "[1]",
                        }),
                        day.isUpcoming && css({ border: "[1px solid #523c02]", rounded: "full" }),
                      )}
                    >
                      <span
                        className={cx(
                          css({
                            textStyle: "subheading.3xs",
                            color: "text.secondary",
                          }),
                          day.isUpcoming && css({ color: "text.primary" }),
                        )}
                      >
                        {day.date}
                      </span>
                      {day.traded && (
                        <span
                          className={css({
                            position: "absolute",
                            top: "0",
                            right: "[-2px]",
                            w: "[5px]",
                            h: "[5px]",
                            rounded: "full",
                            bg: "[rgba(182, 133, 0, 1)]",
                          })}
                        />
                      )}
                    </div>
                  ))}
                </div>

                {/* Weekly status icon */}
                <div
                  className={css({
                    w: "16",
                    h: "16",
                    ml: { base: "8", lg: "16" },
                    flexShrink: "0",
                  })}
                >
                  {weeklyStatus[weekIndex] === "complete" ? (
                    <IconCheckCircleFill
                      className={css({ color: "brandYellow.550", w: "full", h: "full" })}
                    />
                  ) : weeklyStatus[weekIndex] === "incomplete" ? (
                    <IconXCircleFill
                      className={css({ color: "[#3B3733]", w: "full", h: "full" })}
                    />
                  ) : (
                    <IconCircle className={css({ color: "[#3B3733]", w: "full", h: "full" })} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}
